import { View } from './types';
import { toDate, startOfPeriod, addPeriod, getPeriodNumber, periodLabel, withinPeriod } from '../../utils/dateUtils';
import { OverallTypeFilter } from '../../stores/appFiltersStore';
import { RevenueSourceType } from '../../types/RevenueSourceType';
import { getItemTotalRevenue } from '../../utils/revenueAddOns';

// Re-export date utilities for backward compatibility
export { toDate, startOfPeriod, addPeriod, getPeriodNumber, periodLabel, withinPeriod };

export const COLORS: Record<string, string> = {
  trivecgroup: "#7532a8", // Purple for Trivec Group
  tebi: "#00cc8e", // Green for Tebi
};

export const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2
});

export const eur = (n?: number) => currencyFormatter.format(n ?? 0);

export function getTotalRevenue(revenueHistory: any, showExVat: boolean = true, useItemSums: boolean = false): number {
  if (useItemSums) {
    // Sum individual items (base + their nested choices) instead of category totals.
    // After removing the `Add-Ons` synthetic category, choice revenue lives only
    // under `item.choices` — `getItemTotalRevenue` rolls it back into the per-item total.
    return revenueHistory.revenueData.categories.reduce(
      (total: number, category: any) => {
        const itemSum = category.items.reduce(
          (itemTotal: number, item: any) => itemTotal + getItemTotalRevenue(item, showExVat),
          0
        );
        return total + itemSum;
      },
      0
    );
  }

  // Non-`useItemSums` branch: `category.price`/`exVat` already includes base+choices
  // (Cashdesk `buildCategory` sums them in; non-Cashdesk sources have no choices).
  return revenueHistory.revenueData.categories.reduce(
    (total: number, category: any) => total + (showExVat ? (category.exVat || 0) : (category.price || 0)),
    0
  );
}

export function revenueInPeriod(
  revenueHistory: any[] = [],
  start: Date,
  view: View,
  customStartDate?: Date | null,
  customEndDate?: Date | null
): any[] {
  return revenueHistory
    .filter(r => withinPeriod(toDate(r.date), start, view, customStartDate, customEndDate))
    .sort((a, b) => +toDate(b.date) - +toDate(a.date));
}

export function filterRevenueByRestaurantSections(revenue: any[], sectionFilters: string[]): any[] {
  // Note: Sections are no longer part of revenue data. This filter is deprecated.
  // It will be replaced with filterRevenueBySourceType in the future.
  // For now, return all revenue regardless of section filters.
  return revenue;
}

/**
 * Filter revenue by overall type (all/food/drinks/take-away/etc.)
 * This filters based on the revenueSourceType field
 */
export function filterRevenueByOverallType(revenue: any[], overallTypeFilter: OverallTypeFilter): any[] {
  if (overallTypeFilter === 'all') return revenue;

  // Convert filter value to enum format (e.g., 'take-away' -> 'TAKE_AWAY')
  const enumValue = overallTypeFilter.toUpperCase().replace(/-/g, '_');

  return revenue.filter(rev => rev.revenueSourceType === enumValue);
}

export function filterRevenueByCategories(revenue: any[], categoryFilters: string[]): any[] {
  if (categoryFilters.length === 0) return revenue;
  
  return revenue.map(rev => {
    // Filter categories and items within each revenue record
    const filteredCategories = rev.revenueData.categories.filter((category: any) =>
      categoryFilters.includes(category.name)
    );
    
    return {
      ...rev,
      revenueData: {
        ...rev.revenueData,
        categories: filteredCategories
      }
    };
  }).filter((rev: any) => rev.revenueData.categories.length > 0); // Remove records with no matching categories
}

export function filterRevenueByRestaurants(
  revenue: any[],
  restaurantFilters: string[]
): any[] {
  if (restaurantFilters.length === 0) return revenue;

  return revenue.filter(rev => {
    // Use direct restaurantId from revenue data (enriched from the revenue source map on load)
    return rev.restaurantId && restaurantFilters.includes(rev.restaurantId);
  });
}

/**
 * Source-name key: trimmed + lowercased. `restaurant_revenue_sources.restaurant_name` is free
 * text a super-admin types per row, so two rows for one venue can differ in casing/spacing and
 * must still collapse to a single filter option.
 */
const revenueSourceNameKey = (name: string): string => (name ?? '').trim().toLowerCase();

/**
 * The venues behind a restaurant's revenue, as filter options — DISTINCT source NAMES, not source
 * rows. One venue can own several rows: untable's partner "bar POP" has a FOOD row (its food, kept
 * 100%) and an OTHER row (cocktails + soda, kept 50%), so filtering on the name keeps a venue's
 * halves together. Sorted, and the first spelling of a name wins as the label.
 */
export function getAvailableRevenueSourceNames(revenueSources: any[]): string[] {
  const byKey = new Map<string, string>();
  revenueSources.forEach(rs => {
    const name = (rs?.restaurantName ?? '').trim();
    if (!name) return;
    const key = revenueSourceNameKey(name);
    if (!byKey.has(key)) byKey.set(key, name);
  });
  return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
}

/**
 * Keep only revenue that came from the selected venues. Empty filter = every source.
 * Matches on the source row's name (via `restaurantRevenueSourceId`), so selecting "bar POP"
 * returns both of its source rows.
 */
export function filterRevenueBySources(
  revenue: any[],
  revenueSourceFilters: string[],
  revenueSources: any[]
): any[] {
  if (revenueSourceFilters.length === 0) return revenue;

  const selectedKeys = new Set(revenueSourceFilters.map(revenueSourceNameKey));
  const selectedSourceIds = new Set(
    revenueSources
      .filter(rs => selectedKeys.has(revenueSourceNameKey(rs?.restaurantName ?? '')))
      .map(rs => rs.id)
  );
  // Nothing matched — a filter left over from another restaurant, or a renamed source. Treat it
  // as no filter: the dropdown is hidden on a single-source restaurant, so filtering to €0 here
  // would be an empty page with no visible control to clear. The pages prune stale names too.
  if (selectedSourceIds.size === 0) return revenue;

  return revenue.filter(rev => selectedSourceIds.has(rev.restaurantRevenueSourceId));
}

export function filterRevenueByMenuCategories(
  revenue: any[], 
  menuCategoryFilters: string[], 
  costPriceAnalyses: any[],
  categories?: any[]
): any[] {
  if (menuCategoryFilters.length === 0) return revenue;
  
  // Find the default (unassigned) category ID
  const defaultCategoryId = categories?.find(cat => cat.isDefault)?.id;
  const isDefaultCategorySelected = defaultCategoryId && menuCategoryFilters.includes(defaultCategoryId);
  
  // Create a mapping of menu item names to their category IDs
  const itemToCategoryMap = new Map<string, string>();
  costPriceAnalyses.forEach(analysis => {
    if (analysis.categoryId) {
      itemToCategoryMap.set(analysis.menuItemName, analysis.categoryId);
    }
  });
  
  return revenue.map(rev => {
    // Filter items within each category based on menu category assignments
    const filteredCategories = rev.revenueData.categories.map((category: any) => {
      const filteredItems = category.items.filter((item: any) => {
        const assignedCategoryId = itemToCategoryMap.get(item.name);
        
        // If item has an assigned category, check if it matches the filter
        if (assignedCategoryId) {
          return menuCategoryFilters.includes(assignedCategoryId);
        }
        
        // If item has no category and default category is selected, include it
        return isDefaultCategorySelected;
      });
      
      return {
        ...category,
        items: filteredItems
      };
    }).filter((category: any) => category.items.length > 0);
    
    return {
      ...rev,
      revenueData: {
        ...rev.revenueData,
        categories: filteredCategories
      }
    };
  }).filter((rev: any) => rev.revenueData.categories.length > 0);
}

// Helper function to extract available restaurants from revenue data
export function getAvailableRestaurants(revenueData: any[]): { id: string; name: string }[] {
  const restaurantMap = new Map<string, {id: string; name: string}>();

  revenueData.forEach(r => {
    if (r.restaurantId && r.restaurantName) {
      restaurantMap.set(r.restaurantId, { id: r.restaurantId, name: r.restaurantName });
    }
  });

  const restaurants: { id: string; name: string }[] = [];
  restaurantMap.forEach(r => restaurants.push(r));
  return restaurants.sort((a, b) => a.name.localeCompare(b.name));
}

// Helper function to extract available sections from revenue data
// Note: Sections are no longer part of revenue data. This function is deprecated.
// It will be replaced with getAvailableRevenueSourceTypes in the future.
export function getAvailableSections(revenueData: any[]): { id: string; name: string }[] {
  return [];
}

// Helper function to extract available categories from revenue data
export function getAvailableCategories(revenueData: any[]): string[] {
  return Array.from(new Set(
    revenueData.flatMap(r => r.revenueData.categories.map((c: any) => c.name))
  )).sort();
}

// Helper function to get available overall type options including dynamic revenue source types
// Takes revenue sources from the restaurant store, not from filtered data
export function getAvailableOverallTypeOptions(revenueSources: any[]): string[] {
  const baseOptions = ['all', 'food', 'drinks'];

  // Get unique revenue source types from the restaurant's revenue sources
  const revenueSourceTypes = Array.from(new Set(
    revenueSources
      .map(rs => rs.revenueSourceType)
      .filter(type => type && type !== RevenueSourceType.FOOD && type !== RevenueSourceType.DRINKS)
  ));

  // Convert source types to lowercase for consistency with filter values
  const additionalOptions = revenueSourceTypes.map(type => type.toLowerCase().replace(/_/g, '-'));

  return [...baseOptions, ...additionalOptions];
}

/**
 * Get available menu categories from filtered revenue data
 * Extracts unique item names from revenue data and matches them to cost price analyses
 * to determine which menu categories are present in the filtered data
 * 
 * @param revenueData - Filtered revenue data
 * @param costPriceAnalyses - Cost price analyses for menu category mapping
 * @param allCategories - All available categories from the store
 * @returns Array of menu categories that have at least one item in the filtered revenue data
 */
export function getAvailableMenuCategories(
  revenueData: any[],
  costPriceAnalyses: any[],
  allCategories: any[]
): { id: string; name: string }[] {
  // Extract all unique item names from the filtered revenue data
  const itemNames = new Set<string>();
  revenueData.forEach(rev => {
    rev.revenueData.categories.forEach((category: any) => {
      category.items.forEach((item: any) => {
        itemNames.add(item.name);
      });
    });
  });

  // Create a mapping of menu item names to their category IDs
  const itemToCategoryMap = new Map<string, string>();
  costPriceAnalyses.forEach(analysis => {
    if (analysis.categoryId) {
      itemToCategoryMap.set(analysis.menuItemName, analysis.categoryId);
    }
  });

  // Find the default (unassigned) category ID
  const defaultCategoryId = allCategories.find(cat => cat.isDefault)?.id;

  // Collect category IDs that have items in the filtered data
  const categoryIdsInData = new Set<string>();
  itemNames.forEach(itemName => {
    const categoryId = itemToCategoryMap.get(itemName);
    if (categoryId) {
      categoryIdsInData.add(categoryId);
    } else if (defaultCategoryId) {
      // Items without a category assignment belong to the default category
      categoryIdsInData.add(defaultCategoryId);
    }
  });

  // Filter all categories to only include those present in the filtered data
  return allCategories
    .filter(cat => categoryIdsInData.has(cat.id))
    .map(cat => ({
      id: cat.id,
      name: cat.name
    }));
}